import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import Loader from 'tailwind-components/Loader/Loader';
import {
  getCarrierAPIName,
  getCarriers,
  getConfirmedStates,
  getPendingStates,
  isAgentAppointedWithCarrier
} from 'helpers/appointments/appointments';
import { getCarrierAppointmentsSidebarTitle } from './helpers';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';
import Statuses from './Statuses';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import useFlexRowWrapped from 'hooks/useFlexRowWrapped';
import AppointmentTabs from './AppointmentTabs';
import CarrierAppointmentsMainSection from './CarrierAppointmentsMainSection';
import CarrierAppointmentsSidebarBody from './CarrierAppointmentsSidebarBody';
import { cn } from 'lib/utils';
import {
  APPOINTED_CARRIERS_TAB_ID,
  carrierBelongsToTab,
  getAppointedCarrierNameSet,
  getCarrierHomeTabId,
  getCarrierParentGroupTabIds,
  getCarrierPartnersMapFromCarriers,
  getFullStateMapForDisplay,
  getGroupedCarriersByParent,
  isCarrierDisabledByAgencyPreferences
} from 'helpers/carriers/carriers';
import { isSuncareAppointmentBlocked } from 'helpers/suncare/suncare';
import useTabUrlState from 'hooks/useTabUrlState';
import useCarrierTypeFilter from 'hooks/useCarrierTypeFilter';
import { showAlert, toggleSidebar } from 'state/actions/site';

const ACTION_NEEDED_TAB = 'action-needed';
const BY_STATE_TAB = 'by-state';

const INITIAL_ACTIVE_CARRIER = {
  name: '',
  carrier: '',
  confirmed: [],
  pending: [],
  carrierConsidersStates: false
};

export default function CarrierAppointments({
  user,
  getAppointmentsViewData,
  createAppointmentRequest,
  setLocalLoader
}) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    actionableAppointments,
    requestedAppointments,
    carriersArray,
    requestedAppointmentsByCarrier,
    carriers: carriersByType,
    isLoadingAppointmentsData
  } = useSelector(state => state.appointments);
  const [activeCarrier, setActiveCarrier] = useState(INITIAL_ACTIVE_CARRIER);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hasActionableAppts = actionableAppointments.length > 0;
  // tab synced to the URL (?tab); action-needed only exists while there are actionable appts
  const tabIds = useMemo(
    () => [
      APPOINTED_CARRIERS_TAB_ID,
      ...getCarrierParentGroupTabIds(carriersArray),
      ...(hasActionableAppts ? [ACTION_NEEDED_TAB] : []),
      BY_STATE_TAB
    ],
    [carriersArray, hasActionableAppts]
  );
  // read-only here; all writes go through updateUrlParams so tab + carrier never clobber.
  // writeDefaultToUrl is off because the actionable effect writes the smart default itself.
  const [currentTab] = useTabUrlState({
    param: 'tab',
    tabIds,
    defaultTabId: APPOINTED_CARRIERS_TAB_ID,
    writeDefaultToUrl: false
  });
  const [mapSelectedStateCode, setMapSelectedStateCode] = useState(null);
  // true once the user picks a tab, or when the URL arrived with an explicit tab/carrier —
  // in those cases the Action Needed auto-jump should not override the choice
  const tabPinnedRef = useRef(
    Boolean(searchParams.get('tab') || searchParams.get('carrier'))
  );

  const cancelTokenSource = axios.CancelToken.source();

  const appointedAndPendingCarriers = useMemo(
    () =>
      getCarriers(carriersByType, requestedAppointments, user, [
        'confirmed',
        'pending'
      ]),
    [carriersByType, requestedAppointments, user]
  );

  const appointedCarrierNames = useMemo(
    () => getAppointedCarrierNameSet(appointedAndPendingCarriers),
    [appointedAndPendingCarriers]
  );

  const groupedCarriers = useMemo(
    () =>
      getGroupedCarriersByParent(
        carriersArray,
        currentTab,
        appointedCarrierNames
      ),
    [carriersArray, currentTab, appointedCarrierNames]
  );

  // by-state map only: narrows the map + state sidebar, not the carrier lookups
  const carrierTypeFilter = useCarrierTypeFilter(carriersArray);

  const carrierPartnersByState = useMemo(
    () =>
      getCarrierPartnersMapFromCarriers(
        carrierTypeFilter.filteredCarriers,
        user,
        requestedAppointments
      ),
    [carrierTypeFilter.filteredCarriers, user, requestedAppointments]
  );

  const carrierPartnersMap = useMemo(
    () => getFullStateMapForDisplay(carrierPartnersByState),
    [carrierPartnersByState]
  );

  const carrierParam = searchParams.get('carrier');
  const showSidebar =
    currentTab === BY_STATE_TAB
      ? Boolean(mapSelectedStateCode || activeCarrier.name)
      : Boolean(
          activeCarrier.name &&
            (currentTab !== ACTION_NEEDED_TAB || carrierParam)
        );

  const isBelowLg = useBelowBreakpoint('lg');
  const isLgAndUp = !isBelowLg;
  // hide the header while a specific carrier is open
  const showAppointmentsStickyHeader = !activeCarrier.name;
  const [headerRowRef, isHeaderRowWrapped] = useFlexRowWrapped(
    isLgAndUp && showAppointmentsStickyHeader
  );
  const statusesCompactLayout = isBelowLg || isHeaderRowWrapped;
  const resetMap = currentTab !== BY_STATE_TAB || !activeCarrier.name;
  const showByStateMap = currentTab === BY_STATE_TAB;
  const mapStateCarrierNames =
    mapSelectedStateCode == null
      ? []
      : carrierPartnersByState[mapSelectedStateCode]?.carriers ?? [];
  const showByStateCarrierList =
    showByStateMap && !activeCarrier.name && Boolean(mapSelectedStateCode);
  const hasPendingStates = Array.isArray(activeCarrier.pending)
    ? activeCarrier.pending.length > 0
    : Boolean(activeCarrier.pending);
  const isSuncareAppointmentBlockedForCarrier = isSuncareAppointmentBlocked(
    user,
    activeCarrier.name
  );
  const isSomewhatAppointed =
    isAgentAppointedWithCarrier(activeCarrier.name, user) ||
    hasPendingStates ||
    isSuncareAppointmentBlockedForCarrier;

  // single setSearchParams so tab + carrier writes in the same event don't clobber
  // each other (react-router's functional updater sees the current, not pending, params)
  const updateUrlParams = useCallback(
    updates => {
      // build from the live URL so tab + carrier writes in the same tick compose
      const next = new URLSearchParams(window.location.search);
      if ('tab' in updates) {
        if (updates.tab) next.set('tab', updates.tab);
        else next.delete('tab');
      }
      if ('carrier' in updates) {
        if (updates.carrier) next.set('carrier', updates.carrier);
        else next.delete('carrier');
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const updateCarrierSearchParam = useCallback(
    carrierName => updateUrlParams({ carrier: carrierName }),
    [updateUrlParams]
  );

  // sets the sidebar/active-carrier React state for a carrier (no URL writes)
  const applyActiveCarrier = useCallback(
    carrierData => {
      const name = carrierData.name;
      setSidebarOpen(true);
      dispatch(toggleSidebar(false));

      const carrierNoSpaces = getCarrierAPIName(name);
      const carrierReqAppts = requestedAppointmentsByCarrier[name] ?? [];

      const carrierConsidersStates = carrierData.enableStateRequestsIfAppointed
        ? isAgentAppointedWithCarrier(name, user)
        : carrierData.considerStates === 'Yes' &&
          carrierData.statesOffered.length > 0;

      let confirmed;
      if (carrierConsidersStates) {
        confirmed = getConfirmedStates(name, user);
      } else if (Array.isArray(user[carrierNoSpaces])) {
        confirmed = user[carrierNoSpaces];
      } else {
        confirmed = Boolean(user[carrierNoSpaces]);
      }

      const pending = carrierConsidersStates
        ? getPendingStates(carrierReqAppts, confirmed)
        : carrierReqAppts.length > 0 || isSuncareAppointmentBlocked(user, name);

      const hasOpenTransferRequests = carrierReqAppts.some(appt =>
        appt?.request_type?.includes('Transfer')
      );

      const isAgencyDisabled = isCarrierDisabledByAgencyPreferences(
        carrierData,
        user
      );

      setActiveCarrier(prev => ({
        ...prev,
        name,
        pending,
        confirmed,
        carrier: name,
        carrierData: carrierData,
        statesOffered: carrierData.statesOffered,
        hasOpenTransferRequests,
        areApptsDisabled: carrierData.appointmentsDisabled,
        carrierConsidersStates,
        isAgencyDisabled
      }));
    },
    [dispatch, requestedAppointmentsByCarrier, user]
  );

  const onMarketplaceClick = useCallback(
    name => {
      const carrierData = carriersArray.find(carrier => carrier.name === name);
      if (!carrierData) {
        dispatch(
          showAlert({
            message: `Carrier "${name}" does not exist.`,
            code: 'error'
          })
        );
        updateUrlParams({ carrier: null });
        return;
      }

      applyActiveCarrier(carrierData);
      // clicking a carrier in the Action Needed grid moves it to the appointed-carriers tab
      updateUrlParams({
        carrier: name,
        ...(currentTab === ACTION_NEEDED_TAB
          ? { tab: APPOINTED_CARRIERS_TAB_ID }
          : {})
      });
    },
    [applyActiveCarrier, carriersArray, currentTab, dispatch, updateUrlParams]
  );

  /** Keep sidebar confirmed/pending in sync after refreshUser + appointments (e.g. close request). */
  useEffect(() => {
    const name = activeCarrier.name;
    if (!name) return;

    const carrierData = carriersArray.find(c => c.name === name);
    if (!carrierData) {
      if (carriersArray.length > 0) {
        setActiveCarrier(INITIAL_ACTIVE_CARRIER);
        updateCarrierSearchParam(null);
      }
      return;
    }

    const carrierNoSpaces = getCarrierAPIName(name);
    const carrierReqAppts = requestedAppointmentsByCarrier[name] ?? [];

    const nextCarrierConsidersStates =
      carrierData.enableStateRequestsIfAppointed
        ? isAgentAppointedWithCarrier(name, user)
        : carrierData.considerStates === 'Yes' &&
          carrierData.statesOffered.length > 0;

    let nextConfirmed;
    if (nextCarrierConsidersStates) {
      nextConfirmed = getConfirmedStates(name, user);
    } else if (Array.isArray(user[carrierNoSpaces])) {
      nextConfirmed = user[carrierNoSpaces];
    } else {
      nextConfirmed = Boolean(user[carrierNoSpaces]);
    }

    const nextPending = nextCarrierConsidersStates
      ? getPendingStates(carrierReqAppts, nextConfirmed)
      : carrierReqAppts.length > 0 || isSuncareAppointmentBlocked(user, name);

    const nextHasOpenTransferRequests = carrierReqAppts.some(appt =>
      appt?.request_type?.includes('Transfer')
    );

    const isAgencyDisabled = isCarrierDisabledByAgencyPreferences(
      carrierData,
      user
    );

    setActiveCarrier(prev => {
      if (prev.name !== name) return prev;
      return {
        ...prev,
        pending: nextPending,
        confirmed: nextConfirmed,
        carrierData,
        statesOffered: carrierData.statesOffered,
        hasOpenTransferRequests: nextHasOpenTransferRequests,
        areApptsDisabled: carrierData.appointmentsDisabled,
        carrierConsidersStates: nextCarrierConsidersStates,
        isAgencyDisabled
      };
    });
  }, [
    activeCarrier.name,
    carriersArray,
    requestedAppointmentsByCarrier,
    user,
    updateCarrierSearchParam
  ]);

  useEffect(() => {
    if (!carriersArray.length) return;

    const carrierParam = searchParams.get('carrier');
    if (!carrierParam) return;

    const decodedCarrierParam = carrierParam.trim();
    if (!decodedCarrierParam) {
      updateCarrierSearchParam(null);
      return;
    }

    const carrierData =
      carriersArray.find(carrier => carrier.name === decodedCarrierParam) ||
      carriersArray.find(
        carrier =>
          carrier.name.toLowerCase() === decodedCarrierParam.toLowerCase()
      );

    if (!carrierData) {
      dispatch(
        showAlert({
          message: `Carrier "${decodedCarrierParam}" does not exist.`,
          code: 'error'
        })
      );
      updateCarrierSearchParam(null);
      return;
    }

    if (activeCarrier.name !== carrierData.name) {
      applyActiveCarrier(carrierData);
      // select the tab the carrier lives in; if ?tab is present but the carrier
      // doesn't belong to it, correct to the carrier's home tab (single URL write)
      const urlTab = searchParams.get('tab');
      const targetTab =
        urlTab &&
        carrierBelongsToTab(carrierData, urlTab, appointedCarrierNames)
          ? urlTab
          : getCarrierHomeTabId(carrierData, appointedCarrierNames);
      updateUrlParams({
        carrier: carrierData.name,
        ...(targetTab ? { tab: targetTab } : {})
      });
    }
  }, [
    activeCarrier.name,
    appointedCarrierNames,
    applyActiveCarrier,
    carriersArray,
    dispatch,
    searchParams,
    updateUrlParams
  ]);

  useEffect(() => {
    // don't override a tab the user pinned (clicked) or one that arrived in the URL
    if (tabPinnedRef.current || searchParams.get('carrier')) return;
    // write the smart default to the URL: Action Needed when there are actionable
    // appts, otherwise the appointed-carriers default
    updateUrlParams({
      tab: hasActionableAppts ? ACTION_NEEDED_TAB : APPOINTED_CARRIERS_TAB_ID
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActionableAppts]);

  useEffect(() => {
    getAppointmentsViewData(setLocalLoader, cancelTokenSource, true);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  function handleResetActiveCarrier() {
    updateCarrierSearchParam(null);
    setActiveCarrier(INITIAL_ACTIVE_CARRIER);
    let selecteState = mapSelectedStateCode;
    if (resetMap) selecteState = null;
    setMapSelectedStateCode(selecteState);
    if (selecteState) setSidebarOpen(true);
  }

  function onMapStateClick(stateCode) {
    const entry = carrierPartnersMap[stateCode];
    if (!entry) return;
    setActiveCarrier(INITIAL_ACTIVE_CARRIER);
    updateCarrierSearchParam(null);
    setMapSelectedStateCode(stateCode);
    setSidebarOpen(true);
  }

  // tab bar change: one URL write (set tab, clear carrier unless action-needed) + reset state
  const handleTabChange = id => {
    tabPinnedRef.current = true;
    updateUrlParams({
      tab: id,
      ...(id !== ACTION_NEEDED_TAB ? { carrier: null } : {})
    });
    if (id !== ACTION_NEEDED_TAB) {
      setActiveCarrier(INITIAL_ACTIVE_CARRIER);
      setSidebarOpen(false);
    }
  };

  const mainContent = (
    <SimpleContainer className='h-full min-h-0 gap-4 relative'>
      {showAppointmentsStickyHeader && (
        <div
          ref={headerRowRef}
          className='shrink-0 bg-white rounded-xl flex flex-col flex-wrap gap-4 p-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between sticky top-0 shadow-md z-30'
        >
          <SimpleContainer
            data-tour='carrier-appt-tabs'
            className='justify-between max-w-full'
          >
            <h2 className='bg-white m-0 text-left text-2xl font-semibold max-lg:text-center'>
              Carrier Appointments
            </h2>
            <div data-tour='carrier-appt-tab-buttons'>
              <AppointmentTabs
                includeActionableTab
                currentTab={currentTab}
                setCurrentTab={handleTabChange}
              />
            </div>
          </SimpleContainer>
          <div data-tour='carrier-appt-legend'>
            <Statuses
              reverse={!statusesCompactLayout}
              containerClassName={cn(
                'flex-row justify-between',
                statusesCompactLayout && 'w-full',
                !statusesCompactLayout && 'lg:flex-col'
              )}
            />
          </div>
        </div>
      )}
      <CarrierAppointmentsMainSection
        showByStateMap={showByStateMap}
        carrierPartnersMap={carrierPartnersMap}
        onMapStateClick={onMapStateClick}
        mapSelectedStateCode={mapSelectedStateCode}
        carrierTypeOptions={carrierTypeFilter.options}
        selectedCarrierTypes={carrierTypeFilter.selectedTypes}
        onCarrierTypesChange={carrierTypeFilter.setSelectedTypes}
        groupedCarriers={groupedCarriers}
        onCarrierClick={onMarketplaceClick}
        activeCarrier={activeCarrier}
        handleResetActiveCarrier={handleResetActiveCarrier}
        currentTab={currentTab}
        actionNeededTabId={ACTION_NEEDED_TAB}
        carriers={carriersArray.map(c => c.name)}
        useSimpleCarrierCards={
          currentTab !== ACTION_NEEDED_TAB && currentTab !== BY_STATE_TAB
        }
      />
    </SimpleContainer>
  );

  const sidebarContent = (
    <CarrierAppointmentsSidebarBody
      showByStateCarrierList={showByStateCarrierList}
      mapSelectedStateCode={mapSelectedStateCode}
      hasActiveFilters={carrierTypeFilter.selectedTypes.length > 0}
      mapStateCarrierNames={mapStateCarrierNames}
      onMapStateCarrierClick={onMarketplaceClick}
      activeCarrier={activeCarrier}
      isSomewhatAppointed={isSomewhatAppointed}
      createAppointmentRequest={createAppointmentRequest}
      obFormCompleted={user.Initial_Onboarding_Form_Completed}
      actionableOnly={currentTab === ACTION_NEEDED_TAB}
    />
  );

  const sidebarTitle = getCarrierAppointmentsSidebarTitle({
    currentTab,
    byStateTabId: BY_STATE_TAB,
    activeCarrier,
    mapSelectedStateCode
  });

  return (
    <div className='w-full h-full relative'>
      {/* loader overlay while appointments data reloads in the background (e.g. after a request) */}
      {isLoadingAppointmentsData && <Loader />}
      <ContentWithSidebar
        mainContent={mainContent}
        sidebarContent={showSidebar ? sidebarContent : null}
        sidebarAsSheetOnMobile
        sidebarOpen={sidebarOpen}
        onSidebarOpenChange={setSidebarOpen}
        sidebarTitle={sidebarTitle}
        handleCloseSidebar={() => {
          if (isBelowLg) {
            if (resetMap) setMapSelectedStateCode(null);
            return;
          }
          handleResetActiveCarrier();
          if (!resetMap) setSidebarOpen(true);
        }}
        desktopHeaderClassName='bg-white rounded-t-xl'
        noSidebar={!showSidebar}
        desktopSidebarGridClassName='lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]'
      />
    </div>
  );
}

CarrierAppointments.propTypes = {
  user: propTypes.object,
  getAppointmentsViewData: propTypes.func,
  createAppointmentRequest: propTypes.func,
  setLocalLoader: propTypes.func
};
